import React, { useState } from 'react';

import Validation from '~/util/Validation';
import Button from '~/components/atom/Button';
import Dropdown from '~/components/molecule/Dropdown';
import Catalog from '~/Catalog';
import useOfficeOptions from '~/hooks/useOfficeOptions';
import { useInviteUsersToOfficeMutation } from '~/graphql/types';
import Drawer from '~/components/organism/Drawer';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Body, Heading4 } from '~/components/atom/Typography';
import useTrackEvent from '~/hooks/useTrackEvent';
import { type EmailOption } from '~/components/organism/SendEmailBlock/components/MessageHeader/hooks/useGetEmailOptions';
import hasValue from '~/util/hasValue';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import useCurrentAccount from '~/hooks/useCurrentAccount';
import UserSelection from './components/UserSelection';

type ValidationErrors = {
  selectedOptions: string | undefined;
  officeId: string | undefined;
};

type Props = {
  /** If the sidebar is showing */
  isShowing: boolean;

  /**
   * The office id when adding users to an office on settings/offices/:officeId.
   * When provided, the office dropdown will be hidden. Users suggested will be from outside of this office.
   */
  defaultOfficeId: string | null;

  /** Called when the user is successfully added */
  onUserAdded?: () => void;

  /** Callback to remove the sidebar */
  hide: () => void;
};

export const text = {
  emailLabel: Catalog.emailLabel,
  officeLabel: Catalog.officeLabel,
  addMore: 'voeg meer toe',
  submit: 'Uitnodigen',
  noEmailEntered: Catalog.noEmail,
  noOfficeSelected: Catalog.noOffice,
  invalidEmail: 'Ongeldig e-mailadres in de lijst',
  asideTitle: 'Gebruiker toevoegen',
  asidePara1: 'Voeg hier collegas toe aan het system.',
  asidePara2:
    'Admins kunnen ook gebruikers toevoegen en wijzigingen aanbrengen in bijvoorbeeld de apps & vestigingen.',
  asidePara3:
    'Selecteer een vestiging om gebruikers toe te voegen. Daarna kun je gebruikers uit de lijst selecteren of een nieuw e-mailadres toevoegen.',
  disclaimer: Catalog.mayIncreaseCostsWarning('Gebruikers'),
  userInvitedSuccessMessage: 'Gebruiker(s) succesvol uitgenodigd',
};

const AddUserSidebar: React.FC<Props> = ({
  defaultOfficeId,
  isShowing,
  onUserAdded,
  hide,
}) => {
  const { id: accountId } = useCurrentAccount();
  const addToast = useAddToast();
  const officeOptions = useOfficeOptions();

  const [selectedOfficeId, setSelectedOfficeId] = useState(defaultOfficeId);
  const [selectedOptions, setSelectedOptions] = useState<Array<EmailOption>>(
    [],
  );

  const trackEvent = useTrackEvent();

  const validate = (fields: {
    selectedOptions: Array<EmailOption>;
    officeId: string | null | undefined;
  }) => {
    const errors: ValidationErrors = {
      selectedOptions: undefined,
      officeId: undefined,
    };

    if (Validation.String.isEmpty(fields.selectedOptions))
      errors.selectedOptions = text.noEmailEntered;
    if (Validation.String.isEmpty(fields.officeId))
      errors.officeId = text.noOfficeSelected;
    if (fields.selectedOptions.some(({ isValid }) => isValid === false))
      errors.selectedOptions = text.invalidEmail;

    return errors;
  };

  const validationErrors = validate({
    selectedOptions,
    officeId: selectedOfficeId,
  });

  const [inviteUsersToOffice, { loading }] = useInviteUsersToOfficeMutation();

  const handleSubmit = async () => {
    const users = selectedOptions.map(
      option => option.payload.namedEmail.email,
    );

    if (!selectedOfficeId) {
      throw new Error('No officeId provided');
    }

    return inviteUsersToOffice({
      variables: {
        accountId,
        officeId: selectedOfficeId,
        users,
      },
    }).then(({ data, errors }) => {
      if (errors && errors.length > 0) {
        return addToast([
          formatToastMessage(Catalog.genericUnknownErrorMessageShort, 'danger'),
        ]);
      }

      if (data) {
        if (onUserAdded) onUserAdded();

        addToast([
          formatToastMessage(text.userInvitedSuccessMessage, 'success'),
        ]);

        /** ----- TRACKING: user_invited ----- */
        trackEvent({
          eventName: 'user_invited',
          parameters: {
            user: users.join(','),
            office_id: selectedOfficeId,
          },
        });

        hide();
      }

      return data;
    });
  };

  const defaultOfficeName = defaultOfficeId
    ? officeOptions.find(option => option.payload?.id === defaultOfficeId)
        ?.label
    : null;

  return (
    <Drawer active={isShowing} hideSidebar={hide} title={text.asideTitle}>
      <JustificationContainer
        width="100%"
        gap="base"
        direction="column"
        padding={['base', null]}
      >
        {defaultOfficeName && <Heading4 italic>{defaultOfficeName}</Heading4>}
        <Body>{text.asidePara1}</Body>
        <Body>{text.asidePara2}</Body>
        <Body>{text.asidePara3}</Body>
      </JustificationContainer>

      <JustificationContainer
        dataTestId="add-user-form"
        direction="column"
        gap="base"
      >
        <JustificationContainer
          width="100%"
          gap="s"
          direction="column"
          margin={[null, null, 'base', null]}
        >
          {!defaultOfficeId && (
            <Dropdown
              dataTestId="office-dropdown"
              label={text.officeLabel}
              size="large"
              options={officeOptions}
              selectedOptionIdx={officeOptions.findIndex(
                option => option.payload?.id === selectedOfficeId,
              )}
              onChange={({ option }) => setSelectedOfficeId(option.payload.id)}
              required
            />
          )}
          <UserSelection
            officeId={selectedOfficeId}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            key={selectedOfficeId}
          />
        </JustificationContainer>

        <JustificationContainer width="100%" justification="end">
          <Button
            size="large"
            onClick={handleSubmit}
            appearance="secondary"
            disabled={loading || hasValue(validationErrors)}
            dataTestId="add-new-user-submit"
            label={text.submit}
            loading={loading}
          />
        </JustificationContainer>
        <Body margin={['s', null, null, null]} size="s" italic>
          {text.disclaimer}
        </Body>
      </JustificationContainer>
    </Drawer>
  );
};

export default AddUserSidebar;
